import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Youtube, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const Footer = () => {
  const { t } = useApp();
  const [email, setEmail] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    toast.success(t("nl_thanks"));
    setEmail("");
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A0E1A] border-t border-[#C9A84C]/15 mt-24"
    >
      {/* Newsletter */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          <div>
            <p className="overline">{t("nl_overline")}</p>
            <h3 className="font-display text-3xl lg:text-5xl mt-4 leading-[1.15] text-[#F5F0E8]">
              {t("nl_title")}
            </h3>
            <p className="mt-5 text-[#A0A5B5] font-light leading-relaxed max-w-md">
              {t("nl_sub")}
            </p>
          </div>
          <form onSubmit={handleSubmit} data-testid="newsletter-form" className="space-y-6">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("nl_placeholder")}
              data-testid="newsletter-email-input"
              className="lux-input"
            />
            <button
              type="submit"
              data-testid="newsletter-submit-btn"
              className="btn-gold inline-flex items-center gap-3 px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-medium"
            >
              {t("nl_cta")} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>

      <div className="gold-divider" />

      {/* Main footer */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Link to="/" data-testid="footer-logo" className="inline-block">
              <span className="font-display text-3xl text-[#F5F0E8]">
                Maison <span className="italic text-[#C9A84C]">Privé</span>
              </span>
            </Link>
            <p className="mt-5 text-sm font-light text-[#A0A5B5] leading-relaxed max-w-md">
              {t("footer_disclaimer")}
            </p>
            <p className="mt-6 text-xs italic text-[#C9A84C]/80 max-w-md">
              {t("footer_tagline")}
            </p>
          </div>

          <div className="lg:col-span-2">
            <p className="overline mb-5">{t("footer_quick")}</p>
            <ul className="space-y-3 text-sm font-light">
              {[
                ["/properties", "nav_properties"],
                ["/about", "nav_about"],
                ["/guide", "nav_guide"],
                ["/contact", "nav_contact"],
              ].map(([to, k]) => (
                <li key={k}>
                  <Link
                    to={to}
                    data-testid={`footer-link-${k}`}
                    className="text-[#F5F0E8]/75 hover:text-[#C9A84C] transition-colors"
                  >
                    {t(k)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="overline mb-5">{t("footer_legal")}</p>
            <ul className="space-y-3 text-sm font-light">
              <li>
                <Link
                  to="/privacy"
                  data-testid="footer-privacy"
                  className="text-[#F5F0E8]/75 hover:text-[#C9A84C] transition-colors"
                >
                  {t("footer_privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  data-testid="footer-terms"
                  className="text-[#F5F0E8]/75 hover:text-[#C9A84C] transition-colors"
                >
                  {t("footer_terms")}
                </Link>
              </li>
              <li>
                <Link
                  to="/sitemap"
                  data-testid="footer-sitemap"
                  className="text-[#F5F0E8]/75 hover:text-[#C9A84C] transition-colors"
                >
                  {t("footer_sitemap")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="overline mb-5">{t("footer_connect")}</p>
            <div className="flex gap-3 mb-6">
              {[
                { Icon: Instagram, label: "instagram", href: "https://instagram.com" },
                { Icon: Linkedin, label: "linkedin", href: "https://linkedin.com" },
                { Icon: Youtube, label: "youtube", href: "https://youtube.com" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`footer-social-${label}`}
                  className="w-10 h-10 border border-[#C9A84C]/30 flex items-center justify-center text-[#F5F0E8]/80 hover:bg-[#C9A84C] hover:text-[#0A0E1A] transition-colors"
                  aria-label={label}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <div className="space-y-2 text-xs font-light text-[#A0A5B5]">
              <p data-testid="footer-rera">
                <span className="text-[#C9A84C]">{t("footer_rera_label")}:</span> RERA #00000 (placeholder)
              </p>
              <p data-testid="footer-dld">
                <span className="text-[#C9A84C]">{t("footer_dld_label")}:</span> DLD #00000 (placeholder)
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#C9A84C]/10 flex flex-col sm:flex-row sm:justify-between gap-3 text-xs text-[#A0A5B5]">
          <p>© {new Date().getFullYear()} Maison Privé. All rights reserved.</p>
          <p className="italic">Crafted for the discerning investor.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;